/*
 * Master Admin layout — a sibling to InsurerLayout and BrokerLayout, this
 * layout wraps every /master/* route with a distinct navigation that makes
 * clear the operator is working at the platform tier (above any insurer).
 *
 * A prominent purple accent stripe visually differentiates this portal from
 * insurer/broker portals so operators never confuse tenant-level and
 * platform-level actions.
 */
import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/BackButton';
import { useAuth } from '@/lib/auth';
import { RaptorLogo } from '@/components/RaptorLogo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NotificationBell } from '@/components/NotificationBell';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SideNav, NavItem, useSidebarState } from '@/components/SideNav';
import {
  LayoutDashboard, Building2, Users2, ShieldCheck, LogOut, Settings, ArrowLeftRight,
} from 'lucide-react';

const Item: React.FC<{ to: string; icon: React.ReactNode; label: string; end?: boolean }> = ({ to, icon, label, end }) => (
  <NavItem to={to} end={end} label={label} icon={icon}
    activeClass="bg-white/15 text-white font-semibold border-l-2 border-white"
    idleClass="text-white/70 hover:bg-white/10" />
);

export const MasterLayout: React.FC = () => {
  const { user, role, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async (): Promise<void> => { await logout(); nav('/login', { replace: true }); };
  const { collapsed, toggle } = useSidebarState();

  return (
    <div className="flex-1 flex bg-background overflow-hidden" data-testid="master-layout">
      <SideNav collapsed={collapsed} toggle={toggle} className="shadow-lg"
        style={{ background: 'var(--raptor-navy)' }}
        toggleClassName="bg-white border border-white/20 text-raptor-navy hover:bg-white">
        <div className="h-16 px-4 py-2 flex items-center justify-center border-b border-white/10 flex-shrink-0">
          <RaptorLogo size={40} monochrome="white" slot="sidebar_logo" />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
          <Item to="/master" icon={<LayoutDashboard size={16} />} label="Overview" end />
          <Item to="/master/insurers" icon={<Building2 size={16} />} label="Insurers" />
          <Item to="/master/users" icon={<Users2 size={16} />} label="Users" />
          <Item to="/master/audit" icon={<ShieldCheck size={16} />} label="Audit Log" />
          <Item to="/master/switch" icon={<ArrowLeftRight size={16} />} label="Switch into insurer" />
          <Item to="/master/settings" icon={<Settings size={16} />} label="Platform Settings" />
        </nav>
        <div className="p-3 border-t border-white/10 flex-shrink-0">
          {!collapsed && (
            <div className="p-2 rounded-md text-white/90">
              <div className="text-sm font-semibold truncate">{user?.full_name}</div>
              <div className="text-[11px] text-white/60 truncate">{role?.name}</div>
            </div>
          )}
          <Button variant="ghost" size="sm" className={`w-full mt-2 text-white/80 hover:text-white hover:bg-white/10 ${collapsed ? 'justify-center px-0' : 'justify-start'}`} onClick={doLogout} title={collapsed ? 'Sign out' : undefined}>
            <LogOut size={16} /> {!collapsed && 'Sign out'}
          </Button>
        </div>
      </SideNav>
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-border/70 h-16 px-6 flex items-center justify-between flex-shrink-0 z-10">
          <div className="flex items-center gap-3">
            <BackButton />
            <div>
              <h1 className="font-headline text-raptor-navy text-lg tracking-wide leading-tight">Master Admin</h1>
              <p className="text-xs text-raptor-navy/60 font-body leading-tight">You are above every insurer tenant. Actions here affect the entire platform.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="navy" data-testid="master-badge">Platform</Badge>
            <ThemeToggle />
            <NotificationBell />
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
